import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import React from 'react';
import classnames from 'clsx';
import { Image24Icon } from "../../assets/icons/ah/image-24";
import { resizeToCss } from './image.constants';
import { ImageLevel, ImageResize } from './image.interfaces';
import css from "./image.module.css";
/**
 * The image, includes a fallback image.
 * @prop src: string - the source of the image. May contain a directory or url.
 * @prop alt: string - an alt text for accessibility.
 * @prop resize?: ImageResize - the object-fit of the image. Only 'fill' and 'fit' are allowed by design.
 * @prop level?: ImageLevel - the level of image. Defaults to transparent.
 * @prop placeholderIcon?: ImagePlaceholder - the placeholder icon when image cannot be found. Defaults to default image icon.
 * @prop placeholderClassName?: string - the class name for the placeholder image, avoid using it by default
 * @returns the image element.
 */
const Image = ({ className, placeholderIcon = _jsx(Image24Icon, {}), placeholderClassName, src, srcSet, alt, resize = ImageResize.fit, level = ImageLevel.transparent, ...restProps }) => {
    const [hasError, setHasError] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const handler = () => {
            setHasError(true);
        };
        ref.current?.addEventListener('error', handler, true);
        return () => {
            ref.current?.removeEventListener('error', handler, true);
        };
    }, [ref, src, srcSet]);
    if (hasError || !src) {
        return (_jsx("div", { role: "img", className: classnames(css.root, css.fallback, level && css[`level-${level}`], className, placeholderClassName), "aria-label": alt, ...restProps, children: React.cloneElement(placeholderIcon, {
                className: css.fallbackIcon,
                'aria-hidden': 'true',
            }) }));
    }
    // Could have done via style attribute but it will conflict with the className. Therefore keep it in className.
    const resizeCss = resizeToCss[resize];
    return (_jsx("img", { ref: ref, className: classnames(css.root, resizeCss, level && css[`level-${level}`], className), 
        // An empty string ("") may cause the browser to download the whole page again over the network.
        // To fix this, either do not render the element at all or pass null to src instead of an empty string.
        src: src || undefined, srcSet: srcSet, alt: alt, ...restProps }));
};
Image.displayName = 'Image';
export { Image };
